import { prisma, UserRoleAssignmentEntityType } from "database";
import customPrismaAdapter from "./customPrismaAdapter";
import getEnabledProviders from "./getEnabledProviders";
import updateUserSession from "./updateUserSession";
import type { NextAuthOptions } from "next-auth";
import type { EnabledProviders, AuthConfig } from "../@types";
import { getParticipantInteractionCapabilities } from "database/services";
import { getUserWithRolesV2 } from "database/services";
import { ParticipantInteractionCapabilities } from "../types/next-auth";
import type { AuthRequestLike } from "./request-like";
const DEFAULT_MAX_AGE = 24 * 60 * 60; // 1 day in seconds
const REMEMBER_ME_MAX_AGE = 30 * 24 * 60 * 60; // 30 days in seconds

const ROLE_V2_SUPER_ADMIN = "SUPER_ADMIN";
const ROLE_V2_WHITE_LABEL_SUPER_ADMIN = "WHITE_LABEL_SUPER_ADMIN";
const ROLE_V2_DOOR_AGENT = "DOOR_AGENT";
const ORGANIZER_SCOPED_ROLE_NAMES = new Set<string>([
  "ORGANIZATION_SUPER_ADMIN",
  "ORGANIZATION_OWNER",
  "ORGANIZATION_MANAGER",
  "ORGANIZATION_ADMIN",
  "ORGANIZATION_MEMBER",
]);

type RoleV2SessionContext = {
  roles: string[];
  hasSuperAdminRole: boolean;
  hasWhiteLabelSuperAdminRole: boolean;
  hasOrganizerRole: boolean;
  whiteLabelId: string | undefined;
  organizationIds: string[];
  accessibleEventIds: string[];
};

type UserWithRolesV2 = Awaited<ReturnType<typeof getUserWithRolesV2>>;

const getDefaultRoleV2SessionContext = (): RoleV2SessionContext => ({
  roles: [],
  hasSuperAdminRole: false,
  hasWhiteLabelSuperAdminRole: false,
  hasOrganizerRole: false,
  whiteLabelId: undefined,
  organizationIds: [],
  accessibleEventIds: [],
});

const buildRoleV2SessionContext = (
  user: UserWithRolesV2
): RoleV2SessionContext => {
  if (!user) {
    return getDefaultRoleV2SessionContext();
  }

  const roles = new Set<string>();
  const whiteLabelId = user.accessibleWhiteLabelId;
  const organizationIds = new Set<string>();
  const accessibleEventIds = new Set<string>();

  if (user.roleV2?.name) {
    roles.add(user.roleV2.name);
  }

  if (Array.isArray(user.organizations)) {
    user.organizations.forEach((organization) => {
      if (!organization) {
        return;
      }
      if (organization.id) {
        organizationIds.add(organization.id);
      }

      if (
        Array.isArray(organization.eventIds) &&
        user.roleV2?.name == "ORGANIZATION_OWNER"
      ) {
        organization.eventIds.forEach((eventId) => {
          if (typeof eventId === "string" && eventId.length > 0) {
            accessibleEventIds.add(eventId);
          }
        });
      }
    });
  }

  if (Array.isArray(user.profiles)) {
    user.profiles.forEach((profile) => {
      if (!profile) {
        return;
      }
      const profileRoleName = profile.roleV2?.name;
      if (profileRoleName) {
        roles.add(profileRoleName);
      }
      // Add event IDs for organizer roles
      if (
        typeof profile.eventId === "string" &&
        profile.eventId.length > 0 &&
        profileRoleName &&
        ORGANIZER_SCOPED_ROLE_NAMES.has(profileRoleName)
      ) {
        accessibleEventIds.add(profile.eventId);
      }
      // Add event IDs for DOOR_AGENT role from profiles
      if (
        typeof profile.eventId === "string" &&
        profile.eventId.length > 0 &&
        profileRoleName === ROLE_V2_DOOR_AGENT
      ) {
        accessibleEventIds.add(profile.eventId);
      }
    });
  }

  const hasSuperAdminRole = roles.has(ROLE_V2_SUPER_ADMIN);
  const hasWhiteLabelSuperAdminRole = roles.has(
    ROLE_V2_WHITE_LABEL_SUPER_ADMIN
  );
  const hasOrganizerRole = Array.from(roles).some((role) =>
    ORGANIZER_SCOPED_ROLE_NAMES.has(role)
  );
  const hasDoorAgentRole = roles.has(ROLE_V2_DOOR_AGENT);
  const isDoorAgentAtUserLevel = user.roleV2?.name === ROLE_V2_DOOR_AGENT;

  // For DOOR_AGENT at user level: if they have organizations, event IDs come from organization.eventIds
  // For DOOR_AGENT at profile level: event IDs come from profile.eventId (already collected above)
  // If DOOR_AGENT is at user level and has no organizations/profiles, accessibleEventIds will be empty
  // which means they can access all events (handled by middleware or application logic)

  return {
    roles: Array.from(roles),
    hasSuperAdminRole,
    hasWhiteLabelSuperAdminRole,
    hasOrganizerRole,
    whiteLabelId:
      (hasSuperAdminRole || hasWhiteLabelSuperAdminRole) &&
      typeof whiteLabelId === "string"
        ? whiteLabelId
        : undefined,
    organizationIds: hasOrganizerRole ? Array.from(organizationIds) : [],
    // Return event IDs for both organizer roles and DOOR_AGENT
    accessibleEventIds:
      hasOrganizerRole || hasDoorAgentRole
        ? Array.from(accessibleEventIds)
        : [],
  };
};

export const getAuthOptions = (
  enabledProviders: EnabledProviders,
  config: AuthConfig | undefined = {
    isVercelDeployment: false,
    isProduction: false,
  },
  request?: AuthRequestLike
) => {
  if (!enabledProviders || !enabledProviders.length)
    throw new Error(
      "No providers specified, please specify at least one enabled provider"
    );
  const host = request?.headers.get("host");
  const { isVercelDeployment, isProduction } = config;
  const providers = getEnabledProviders(enabledProviders, request);
  const nextAuthUrl = process.env["NEXTAUTH_URL"];
  if (!nextAuthUrl) {
    throw new Error("NEXTAUTH_URL environment variable is not defined");
  }
  const useSecureCookies = nextAuthUrl.startsWith("https://");
  const cookiePrefix = useSecureCookies ? "__Secure-" : "";

  if (!isProduction) {
    if (host) {
      process.env["NEXTAUTH_URL"] = `http://${host}`;
    }
  } else {
    process.env["NEXTAUTH_URL"] =
      host && useSecureCookies
        ? `https://${host}`
        : host && !useSecureCookies
        ? `http://${host}`
        : nextAuthUrl;
  }

  const options: NextAuthOptions = {
    providers,
    pages: {
      signIn: `/login`,
      verifyRequest: `/login`,
      error: "/login?error=", // Error code passed in query string as
    },
    adapter: customPrismaAdapter(prisma, request),
    session: {
      strategy: "jwt",
      maxAge: REMEMBER_ME_MAX_AGE, // Use longer maxAge, actual expiry controlled by JWT
    },
    cookies: {
      sessionToken: {
        name: `${cookiePrefix}next-auth.session-token`,
        options: {
          httpOnly: true,
          sameSite: "lax",
          path: "/",
          // Multi-tenant cookie domain with tenant isolation:
          // - undefined = cookies scoped to exact hostname (e.g., tenant1.sajilni.com)
          // - This ensures each tenant/subdomain has separate, isolated session cookies
          // - tenant1.sajilni.com and admin.sajilni.com will NOT share cookies (security isolation)
          domain: undefined,
          secure: useSecureCookies,
        },
      },
    },
    callbacks: {
      jwt: async ({ token, user, trigger, session }) => {
        // Persist the user to the token right after signin
        if (user && "eventSlug" in user && typeof user.eventSlug === "string") {
          token.eventSlug = user.eventSlug;
        }
        if (!token?.user?.id && !user?.id) {
          throw new Error("No user id found in token or user");
        }
        if (user) {
          // Store only essential fields instead of entire user object
          token.user = {
            id: user.id,
            email: user.email,
            name: user.name,
            image: "image" in user ? (user as any).image : null,
            roles: "roles" in user ? (user as any).roles : [],
            rolesV2: "rolesV2" in user ? (user as any).rolesV2 : [],
          } as any;
          const rememberMe = user?.rememberMe;
          if (typeof rememberMe !== "undefined") {
            token.rememberMe = rememberMe;
            // Set JWT expiry based on rememberMe
            const maxAge = rememberMe ? REMEMBER_ME_MAX_AGE : DEFAULT_MAX_AGE;
            token.exp = Math.floor(Date.now() / 1000) + maxAge;
          }
        }
        if (user?.id) {
          const accessibleWhiteLabelId =
            "accessibleWhiteLabelId" in user &&
            typeof user.accessibleWhiteLabelId === "string"
              ? user.accessibleWhiteLabelId
              : undefined;

          const userWithRolesV2 = await getUserWithRolesV2(
            user.id,
            accessibleWhiteLabelId
          );
          const roleV2Context = buildRoleV2SessionContext(userWithRolesV2);
          token.roleV2Context = roleV2Context;

          token.user = {
            ...token.user,
            rolesV2: roleV2Context.roles,
            whiteLabelId: roleV2Context.whiteLabelId,
            accessibleWhiteLabelId: accessibleWhiteLabelId, // Store accessibleWhiteLabelId for middleware access
            organizationIds: roleV2Context.organizationIds,
            accessibleEventIds: roleV2Context.accessibleEventIds,
          } as any;
        }
        if (trigger === "update") {
          await updateUserSession(session, token);
        }

        return token;
      },
      session: async ({ session, token }) => {
        // Set session expiry based on rememberMe
        const maxAge = token.rememberMe ? REMEMBER_ME_MAX_AGE : DEFAULT_MAX_AGE;
        const expiryDate = Date.now() + maxAge * 1000;
        session.expires = new Date(expiryDate).toISOString();
        session.rememberMe = token.rememberMe as boolean;

        const accessToken = await request?.cookies?.get(
          `${cookiePrefix}next-auth.session-token`
        )?.value;
        // get all cookies from request as string
        const cookie = await request?.cookies?.toString();
        if (!session?.user?.id && !token?.user?.id) {
          throw new Error("No user id found in session");
        }
        session.user = {
          ...session.user,
          ...token.user,
          id: token.sub as string,
        };
        const roleV2Context =
          token.roleV2Context || getDefaultRoleV2SessionContext();
        session.user.rolesV2 = roleV2Context.roles;
        session.user.whiteLabelId = roleV2Context.whiteLabelId;
        // Store accessibleWhiteLabelId from token for middleware access
        (session.user as any).accessibleWhiteLabelId = (
          token.user as any
        )?.accessibleWhiteLabelId;
        session.user.organizationIds = roleV2Context.organizationIds;
        session.user.accessibleEventIds = roleV2Context.accessibleEventIds;
        session.roleV2Context = roleV2Context;
        session.whiteLabelId = roleV2Context.whiteLabelId;
        session.organizationIds = roleV2Context.organizationIds;
        session.accessibleEventIds = roleV2Context.accessibleEventIds;
        if (typeof token.eventSlug === "string" && token.eventSlug.length > 0) {
          session.eventSlug = token.eventSlug;
          const userCapabilities = await getParticipantInteractionCapabilities(
            token?.user?.id || session?.user?.id,
            token.eventSlug
          );
          session.capabilities =
            userCapabilities as ParticipantInteractionCapabilities;
        }
        return { ...session, jwt: accessToken, cookie };
      },
    },
  };
  return options;
};

export { default, getServerSession, type NextAuthOptions } from "next-auth";
export { decode } from "next-auth/jwt";
export type { EnabledProviders };
export type { AuthRequestLike } from "./request-like";
export * from "./react";
